import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemStyle } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import setADIUserRole from "@NA/Handlers/ADIGetUserRoleHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { setTracking } from "@NA/Overrides/Store/CommonHandlers/SetTracking";
import siteMessage from "@insite/client-framework/SiteMessage"; // This import is from P21 Change
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import useInitializeSentry from "@NA/Hooks/useInitializeSentry";
const mapStateToProps = (state: ApplicationState) => ({
    placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    currentLocation: getLocation(state),
    settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    setBreadcrumbs,
    setADIUserRole,
    setTracking,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    breadcontainer?: InjectableCss;
    customerStatus_Banner?: InjectableCss; // This property is due to P21 Change
    checkoutOrderProcessingBanner?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    skipToContent: {
        skipToContent: {
            css: css`
                z-index: 999999999 !important;
                width: 250px;
                margin: 12px;
            `,
        },
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            > div[class^="Wrapper"]:nth-child(2) {
                height: 80px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            height: 60px;
                        `,
                    css`
                            height: 60px;
                        `,
                    css`
                            height: 60px;
                        `,
                    null,
                    null,
                ])}
                > div[class^="sc"] {
                    height: inherit;
                    align-items: center;
                }
                nav[class^="OverflowWrapper"] {
                    position: unset !important;
                    padding-bottom: 15px;
                    margin-top: 13px;
                }
            }
        `,
    },
    breadcontainer: {
        css: css`
            border: 10px solid red;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    // Below added style property is due to a P21 Change
    customerStatus_Banner: {
        css: css`
            background-color: #ad1212;
            color: white;
            text-align: center;
            padding: 10px 50px 10px 50px;
            font-weight: 400;
        `,
    },
    checkoutOrderProcessingBanner: {
        css: css`
            background-color: #ad1212;
            color: white;
            width: 100%;
            padding: 10px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-weight: 400;
            position: relative;
            z-index: 100;

            .error-content {
                display: flex;
                align-items: center;
                flex: 1;
                gap: 10px;
            }

            .svg-icon {
                width: 20px;
                height: 20px;
                min-width: 20px;
                background-color: white;
                mask: url("https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/np-warning.svg")
                    no-repeat center;
                -webkit-mask: url("https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/np-warning.svg")
                    no-repeat center;
            }

            strong {
                font-size: 18px;
                font-weight: 700;
                margin-right: 10px;
            }

            .divider {
                height: 20px;
                width: 2px;
                background-color: #d8d8d8;
                margin: 0 10px;
            }

            .banner-text {
                font-size: 14px;
                line-height: 1.4;
            }

            a {
                color: white;
                text-decoration: underline;
            }

            .close-button {
                background: none;
                border: none;
                color: white;
                cursor: pointer;
                padding: 0;
                margin-left: 15px;
                height: 24px;
                width: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .close-icon {
                width: 20px;
                height: 20px;
                position: relative;
            }

            .close-icon:before,
            .close-icon:after {
                content: "";
                position: absolute;
                width: 20px;
                height: 2px;
                background-color: white;
                top: 50%;
                left: 0;
            }

            .close-icon:before {
                transform: rotate(45deg);
            }

            .close-icon:after {
                transform: rotate(-45deg);
            }

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: flex-start;

                        .error-content {
                            margin-bottom: 10px;
                        }

                        .close-button {
                            position: absolute;
                            top: 10px;
                            right: 10px;
                        }
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentLocation,
    setBreadcrumbs,
    setADIUserRole,
    setTracking,
    placeOrderErrorMessage,
    settingsCollection,
}: Props) => {

    // sentry configuration
    const isSentryServiceEnabled = settingsCollection.sentrySettingSettings?.enableSentryService;
    if (isSentryServiceEnabled) {
        const sentryEndpointUrl = settingsCollection.sentrySettingSettings?.sentryEndpointUrl;
        const tracePropagationTargets = settingsCollection.sentrySettingSettings?.tracePropagationTargets;
        const tracePropagationRegeEx = settingsCollection.sentrySettingSettings?.tracePropagationRegeEx;
        useInitializeSentry(sentryEndpointUrl, tracePropagationTargets, tracePropagationRegeEx);
    }

    const returnUrl = currentLocation.pathname + currentLocation.search;
    const currentState = useSelector((state: CustomApplicationState) => state);
    const notIncludes = returnUrl.includes("RequestQuoteForm");
    const checkoutURL = returnUrl.includes("ReviewAndPay");
    const ordersURL = returnUrl.includes("/myaccount/Order"); // This is being used byP21
    const confirmReturnsUrl = returnUrl.includes("ConfirmReturns") || returnUrl.includes("Returns/Confirmation"); // This is being used by P21
    const [isErrorBannerVisible, setIsErrorBannerVisible] = useState(true);
    const currentURL =
        (returnUrl.includes("MyAccount") || returnUrl.includes("myaccount")) &&
        !notIncludes &&
        !returnUrl.includes("ChangeCustomer") &&
        !returnUrl.includes("Invoices/Details") &&
        !returnUrl.includes("recoverusernamepassword") &&
        !returnUrl.includes("signin") &&
        !returnUrl.includes("ReturnHistory") &&
        !returnUrl.includes("ReturnDetail") &&
        !returnUrl.includes("VisualNavigationManager") &&
        !returnUrl.includes("FBTUpload") &&
        !returnUrl.includes("MKTUpload") &&
        !returnUrl.includes("shopmybranch");

    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const getCustomerCreditStatus = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore,
    );

    styles.page = {
        fullWidth: [true, true, true, true, true],
        css: css`
            box-shadow: 0 2px 4px 0 hsla(0, 0%, 41%, 0.32);
            border-bottom: 1px solid ${getColor("common.background")};
            position: sticky !important;
            top: 0;
            z-index: 99;
            .skipNavContainer {
                max-width: 1440px;
                margin: 0 auto;
                padding-left: 125px;
            }
            + div[class^="PageStyle"] {
                max-width: 1470px;
                background-color: ${currentURL && ADIColors.white};
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            padding-top: 9px !important;
                            display: ${checkoutURL ? "none" : "block"};
                        `,
                    css`
                            padding-top: 9px !important;
                            display: ${checkoutURL ? "none" : "block"};
                        `,
                    null,
                    null,
                    null,
                ])}
            }
            ~ main[class^="PageStyle"] {
                max-width: 1470px;
                padding: 15px;
                min-height: 600px;
                background-color: ${currentURL && ADIColors.white};
                .lbProduct {
                    opacity: 0.4;
                }
                [data-test-selector="productCarousel_AIProton"]
                    [data-test-selector="productContainer"]
                    .priceItem
                    [data-test-selector="productPrice_unitNetPrice"] {
                    min-height: initial;
                }
                @media only screen and (min-width: 768px) and (max-width: 1079px) {
                    padding: 15px 5px;
                }
                [data-test-selector="productListCardContainerGrid"] {
                    [data-test-selector="productListProductImage"] > a > img::selection,
                    [data-id="checkbox"]::selection {
                        background: transparent;
                    }
                }
            }
            ~ main[data-test-selector^="homePage"] {
                padding: 0px !important;
                max-width: 1600px;
                > div[class^="Wrapper"] {
                    > div[class^="sc"] {
                        min-width: 100%;
                        max-width: 100%;
                    }
                    ${GridWrapper} {
                        ${GridOffset} {
                            ${GridItemStyle}:first-child {
                                padding: 0px;
                            }
                        }
                    }
                }
            }
            ~ main[data-test-selector^="productDetails"] {
                max-width: 100%;
                @media only screen and (min-width: 768px) and (max-width: 1080px) {
                    overflow-x: hidden;
                    padding-left: 25px;
                    padding-right: 25px;
                }

                div[class^="Wrapper"] {
                    > div {
                        max-width: 1440px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    .RecentlyViewed_widget {
                        max-width: 100%;
                        margin-top: 20px;
                        p {
                            font-size: 12px;
                        }
                    }
                }
                header {
                    > div[class^="GridWrapper"] {
                        // padding: 13px 10px 10px 10px !important;
                        //height: 101px;
                        > div[class^="GridOffset"] {
                            div[class^="GridItemStyle"]:nth-of-type(2) {
                                > div[class^="GridWrapper"] {
                                    > div[class^="GridOffset"] {
                                        div[class^="GridItemStyle"]:nth-of-type(2) {
                                            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                                                padding-top: 15px;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    div[class^="GridWrapper"] {
                        > div[class^="GridOffset"] {
                            @media only screen and (max-width: 1080px) {
                                width: 100%;
                                margin: 0px;
                            }
                            div[class^="GridItemStyle"] {
                                @media only screen and (max-width: 1080px) {
                                    padding: 0px;
                                }
                                @media only screen and (max-width: 767px) {
                                    padding: 0px;
                                }
                            }
                        }
                        div[class^="GridItemStyle"]:nth-of-type(3) {
                            div[class^="GridOffset"] {
                                padding-top: 7px;
                                @media only screen and (max-width: 767px) {
                                    padding-top: 0px;
                                }
                            }
                        }
                        #ButtonTabsPDP_pageHeader {
                            border: solid 1px #ddd !important;
                        }
                        div[data-test-selector="productPrice_Wrapper"] {
                            padding-right: 28px !important;
                            margin-top: 8px !important;
                            margin-top: 0px !important;
                            @media only screen and (min-width: 768px) and (max-width: 1080px) {
                                padding-right: 7px !important;
                            }
                            @media only screen and (max-width: 767px) {
                                margin-top: 0px !important;
                                padding-right: 7px !important;
                            }
                            .symbolText {
                                margin-top: 0px;
                                @media only screen and (min-width: 768px) and (max-width: 1080px) {
                                    margin-top: 2px;
                                }
                                @media only screen and (max-width: 767px) {
                                    margin-top: 2.5px;
                                }
                            }
                            .currencyText {
                                margin-top: -4px;
                            }
                        }
                    }
                }
                [data-test-selector="productCarousel_AIProton"] {
                    margin: 0 -10px;
                    width: calc(100% + 20px);
                    max-width: calc(1480px + 10px);
                }
                @media only screen and (max-width: 1080px) {
                    [data-test-selector="productCarousel_AIProton"] {
                        width: 768px;
                        max-width: calc(768px + 10px);
                        margin: 0 auto !important;
                    }
                }
                @media only screen and (max-width: 767px) {
                    [data-test-selector="productCarousel_AIProton"] {
                        width: calc(100% + 10px);
                        margin-left: -5px !important;
                    }
                }
            }
        `,
    };

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const closeErrorBanner = () => {
        setIsErrorBannerVisible(false);
        // Optionally persist this state in localStorage
        if (typeof window !== "undefined") {
            window.localStorage.setItem("isErrorProcessingOrder", "false");
        }
    };

    useEffect(() => {
        setTracking();
        if (session.hasRfqUpdates) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, []);

    useEffect(() => {
        if (session.isAuthenticated && session.userRoles) {
            setADIUserRole({
                currentUserRole: session.userRoles,
            });
        }
    }, [session]);
    // Below useState, & useEffect is being used by P21 ----- P21 Change
    const [showCustomerStatusBanner, setshowCustomerStatusBanner] = useState<any>(false);
    const [showAccountVerificationBanner, setshowAccountVerificationBanner] = useState<any>("");

    useEffect(() => {
        // const getBannerStatus = localStorage.getItem("showCustomerStatusBanner")
        // setshowCustomerStatusBanner(getBannerStatus === "true");

        setshowCustomerStatusBanner(getCustomerCreditStatus?.getcustomerStatus?.showCustomerStatusBanner);
        setshowAccountVerificationBanner(getCustomerCreditStatus?.getcustomerStatus?.creditStatus);
    }, [
        showAccountVerificationBanner,
        getCustomerCreditStatus?.getcustomerStatus?.creditStatus,
        showCustomerStatusBanner,
    ]);

    const isBrowser = typeof window !== "undefined";

    let isCheckoutError = false;

    if (isBrowser) {
        isCheckoutError = window.localStorage.getItem("isErrorProcessingOrder") === "true";
    }

    const afterHeader = React.createRef<HTMLSpanElement>();
    const style = isPendingVerification ? { borderBottom: "0px", boxShadow: "none" } : { borderBottom: "1px" };

    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page as="header" {...styles.page} data-test-selector="header" style={style}>
                <div className="skipNavContainer">
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                </div>

                <EagerLoadingLogoProvider value={true}>
                    {!returnUrl.includes("signinassist") && (
                        <>
                            <StyledWrapper {...styles.container}>
                                <Zone contentId={id} zoneName="SecondaryNavigation" />
                                <Zone contentId={id} zoneName="MainNavigationContainer" />
                            </StyledWrapper>
                            <Zone contentId={id} zoneName="Breadcrumb" />
                        </>
                    )}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />

                {showAccountVerificationBanner !== "O" &&
                    showAccountVerificationBanner !== "" &&
                    showAccountVerificationBanner !== null &&
                    !currentState.components.addressDrawer.navDrawerIsOpen &&
                    !currentState.ADIGlobalData.PricingStatusStore.isSearchBarOpen ? (
                    <Typography as="div" {...styles.customerStatus_Banner}>
                        {siteMessage(
                            "Your account is pending verification, so pricing and other features are unavailable. To expedite the process, email BecomeACustomer@ADIGlobal.com or call 1.888.262.2341.",
                        )}
                    </Typography>
                ) : (
                    showCustomerStatusBanner &&
                    !currentState.components.addressDrawer.navDrawerIsOpen &&
                    !currentState.ADIGlobalData.PricingStatusStore.isSearchBarOpen && (
                        <Typography as="div" {...styles.customerStatus_Banner}>
                            {siteMessage(
                                "We're sorry. Some of your account features have been disabled due to a problem with your account. Please call our Website Support team at 1.877.228.6739 to resolve this issue.",
                            )}
                        </Typography>
                    )
                )}
                {checkoutURL && placeOrderErrorMessage && isErrorBannerVisible && (
                    <Typography as="div" {...styles.checkoutOrderProcessingBanner}>
                        <div className="error-content">
                            <div className="svg-icon"></div>
                            <strong>Error</strong>
                            <div className="divider"></div>
                            <div className="banner-text">
                                <span>
                                    There was an error processing your order, please try again later. For immediate
                                    assistance, email{" "}
                                    <a href="mailto:websupport@adiglobal.com">websupport@adiglobal.com</a> or call us at{" "}
                                    <a href="tel:1.877.228.6739">1.877.228.6739</a> (Mon - Fri. 8:00 a.m. - 7:00 p.m.
                                    ET). We apologize for the temporary interruption of service.
                                </span>
                            </div>
                        </div>
                        <button className="close-button" onClick={closeErrorBanner} aria-label="Close error message">
                            <div className="close-icon"></div>
                        </button>
                    </Typography>
                )}
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
